import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import {
  DEBOUNCE_TIME,
  RequestService,
} from 'src/app/services/request.service';
import * as moment from 'moment';
import * as _ from 'lodash';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import TaskAddressHelper from './task-address-helper';
import Employee from 'src/app/model/product/employee';
import { SnackService } from 'src/app/services/snack.service';
import { TaskList } from 'src/app/model/task-list/task-list';
import { from, merge } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import ProjectHelper from 'src/app/helpers/project-helper';
import { Task, TASK_TYPES } from 'src/app/model/task-list/task';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { LoadingService } from 'src/app/services/loading.service';
import { PlacerNotesDialogComponent } from 'src/app/modals/placer-notes-dialog/placer-notes-dialog.component';
import { Project } from 'src/app/model/project/project';
import { SubProjectTask } from 'src/app/model/task-list/subproject-task';
import { OtherTask } from 'src/app/model/task-list/other-task';
import { LoadTask } from 'src/app/model/task-list/load-task';
import { CreateEditOtherTaskComponent } from '../../modal/create-edit-other-task/create-edit-other-task.component';
import { PrintAddressPipe } from 'src/app/pipes/print-address.pipe';
import { SubProject } from 'src/app/model/project/sub-project';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { EditLoadTaskComponent } from '../../modal/edit-load-task/edit-load-task.component';
import Address from 'src/app/model/project/address';
import DateHelper from 'src/app/helpers/date-helper';
import { DailyPlanningCacheService } from './daily-planning-cache.service';

@Component({
  selector: 'daily-planning-overview',
  templateUrl: './daily-planning-overview.component.html',
  styleUrls: ['./daily-planning-overview.component.scss'],
  providers: [PrintAddressPipe],
})
export class DailyPlanningOverviewComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @ViewChild('table', { static: true }) table: MatTable<Task>;
  displayedColumns: string[];
  weekDates: moment.Moment[];
  selectedDateIndex: number;
  loadingWeek: boolean = true;
  loadingPlacers: boolean = true;
  loadingTaskList: boolean = false;
  weekDifferential: number = 0;
  employeeOptions: Employee[];
  selectedEmployee: Employee;

  taskList: TaskList;
  subProjects: SubProject[];
  addresses: Address[];
  reloadData = new EventEmitter<any>();
  saveTaskListEvent = new EventEmitter<any>();

  private dialogModalRef: MatDialogRef<PlacerNotesDialogComponent>;
  private createEditOtherTaskModelRef: MatDialogRef<CreateEditOtherTaskComponent>;

  constructor(
    private requestService: RequestService,
    private snackService: SnackService,
    private loadingService: LoadingService,
    private printAddressPipe: PrintAddressPipe,
    private modalService: ModalService,
    private dialog: MatDialog,
    private dailyPlanningCacheService: DailyPlanningCacheService
  ) {
    super();
  }

  ngOnInit() {
    this.displayedColumns = [
      'projectId',
      'taskInfo',
      'startTime',
      'timeBudget',
      'productType',
      'taskType',
      'star',
    ];
    this.getPlacers();

    this.initializeWeek();

    this.subscribeGetTaskList();
    this.subscribeSaveTaskList();
  }

  private initializeWeek(): void {
    this.loadingWeek = true;

    const selectedDate = this.dailyPlanningCacheService.getSelectedDate();
    const today = DateHelper.getMomentAtStartOfDay();
    if (selectedDate != null) {
      const initialWeekDifferential = this.getInitialWeekDifferential(
        selectedDate,
        today
      );
      this.changeWeek(initialWeekDifferential);
      this.selectDay(selectedDate);
    } else {
      this.changeWeek(today.weekday() === 6 ? 1 : 0);
      this.selectDay(today);
    }

    this.loadingWeek = false;
  }

  private getInitialWeekDifferential(
    selectedDate: moment.Moment,
    today: moment.Moment
  ): number {
    return moment(selectedDate)
      .startOf('week')
      .diff(moment(today).startOf('week'), 'weeks');
  }

  subscribeGetTaskList() {
    this.addSubscription(
      merge(this.reloadData).pipe(
        startWith({}),
        switchMap(() => {
          this.loadingTaskList = true;
          return this.selectedEmployee != null && this.selectedDateIndex != null
            ? this.requestService.get(
                `/tasklist/${this.selectedEmployee.id}/${this.weekDates[
                  this.selectedDateIndex
                ].format('YYYYMMDD')}`
              )
            : from([{ body: { tasks: [], checked: false } }]);
        }),
        map((data) => {
          return data.body;
        })
      ),
      (data: TaskList) => {
        this.taskList = data;
        this.preProcessTasks();
        this.loadingTaskList = false;
      },
      (err) => {
        if (err instanceof Error) {
          this.snackService.showError(err.message);
        } else {
          this.snackService.showError(err);
        }
      }
    );
  }

  subscribeSaveTaskList() {
    this.addSubscription(
      this.saveTaskListEvent,
      () => {
        this.doSaveTaskList();
      },
      (err) => {},
      DEBOUNCE_TIME
    );
  }

  saveTaskList() {
    this.loadingService.isLoading();
    this.saveTaskListEvent.next();
  }

  doSaveTaskList() {
    for (let i = 0; i < this.taskList.tasks.length; i++) {
      this.taskList.tasks[i].sequenceNumber = i;
    }
    this.addSubscription(
      this.requestService.put(this.taskList, '/tasklist'),
      () => {
        this.loadingService.isSaved();
      }
    );
  }

  changeWeek(weekDifferential: number, byUser = false) {
    if (byUser) {
      this.loadingWeek = true;
    }
    this.weekDifferential += weekDifferential;
    this.weekDates = _.range(0, 6).map((i: number) =>
      DateHelper.getMomentAtStartOfDay()
        .weekday(i)
        .add(this.weekDifferential * 7, 'days')
    );
    if (byUser) {
      this.selectedDateIndex = 0;
      this.dailyPlanningCacheService.setSelectedDate(
        this.weekDates[this.selectedDateIndex]
      );
      this.loadingWeek = false;
      this.reloadData.emit();
    }
  }

  changeSelectedDateIndex(i: number) {
    this.selectedDateIndex = i;
    this.dailyPlanningCacheService.setSelectedDate(this.weekDates[i]);
    this.reloadData.emit();
  }

  selectDay(moment: moment.Moment) {
    moment = moment.day() === 0 ? moment.add(1, 'days') : moment;
    this.selectedDateIndex = this.weekDates.findIndex((d) =>
      moment.isSame(d, 'day')
    );
    this.dailyPlanningCacheService.setSelectedDate(moment);
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }

  getPlacers() {
    this.addSubscription(
      this.requestService.get('/employees/withType/PLACER'),
      (result: any) => {
        this.employeeOptions = result.body;
        this.loadingPlacers = false;
        this.initializeSelectedEmployee();
      },
      (err) => {
        if (err instanceof Error) {
          this.snackService.showError(err.message);
        } else {
          this.snackService.showError(err);
        }
      }
    );
  }

  private initializeSelectedEmployee(): void {
    const selectedEmployeeId =
      this.dailyPlanningCacheService.getSelectedEmployeeId();
    if (selectedEmployeeId != null) {
      this.selectedEmployee = this.employeeOptions.find(
        (employee) => employee.id === selectedEmployeeId
      );
      this.reloadData.emit();
    }
  }

  selectedEmployeeChange() {
    if (this.selectedEmployee != null) {
      this.dailyPlanningCacheService.setSelectedEmployeeId(
        this.selectedEmployee.id
      );
    }
    this.reloadData.emit();
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }

  onListDrop(event: CdkDragDrop<Task[]>) {
    const prevIndex = this.taskList.tasks.findIndex(
      (t) => t.id === event.item.data.id
    );
    moveItemInArray(this.taskList.tasks, prevIndex, event.currentIndex);
    this.table.renderRows();
    this.recalculateTaskSubProjects();
    this.calculateMapAddresses();
    this.saveTaskList();
  }

  taskListCheckedChange(value: boolean) {
    this.taskList.checked = value;
    this.saveTaskList();
  }

  preProcessTasks(): void {
    const subProjects: SubProject[] = [];
    this.taskList.tasks.forEach((t) => {
      if (t.type === TASK_TYPES.SUB_PROJECT_TASK) {
        const subProjectTask = <SubProjectTask>t;
        this.preprocessSubProjectTask(subProjectTask);
        subProjects.push(subProjectTask.subProject);
      } else if (t.type === TASK_TYPES.OTHER_TASK) {
        t.timeBudgetDisplay = t['minuteBudget'];
      } else if (t.type === TASK_TYPES.LOAD_TASK) {
        this.preprocessLoadTask(<LoadTask>t);
      }
    });
    this.subProjects = subProjects;
    this.calculateMapAddresses();
  }

  recalculateTaskSubProjects(): void {
    this.subProjects = this.taskList.tasks
      .filter((t) => t.type === 'SubProjectTask')
      .map((t) => (<SubProjectTask>t).subProject);
  }

  calculateMapAddresses(): void {
    this.addresses = TaskAddressHelper.getMapAddresses(this.taskList.tasks);
  }

  preprocessSubProjectTask(task: SubProjectTask) {
    if (task.subProject != null && task.subProject.project != null) {
      task.titleDisplay = `${ProjectHelper.getType(task.subProject)} project #${
        task.subProject.project.id
      }`;

      this.preprocessCompanies(task);

      if (
        task.subProject.type === 'BaseProject' &&
        CollectionHelper.isPopulated(task.subProject.project.products)
      ) {
        let timeBudgetMinutes = 0;
        task.subProject.project.products.forEach((p) => {
          if (p.productPlacement != null) {
            timeBudgetMinutes +=
              (p.productPlacement.installerHourBudget == null
                ? 0
                : p.productPlacement.installerHourBudget * 60) +
              (p.productPlacement.installerMinuteBudget == null
                ? 0
                : p.productPlacement.installerMinuteBudget);
            p.productPlacement.installerInfo = StringHelper.isEmpty(
              p.productPlacement.installerInfo
            )
              ? 'Geen opmerkingen'
              : p.productPlacement.installerInfo;
          }
        });
        task.timeBudgetDisplay = timeBudgetMinutes.toString();
      }
      if (task.subProject.type !== 'BaseProject') {
        task.subProject.withPlacementNotes = StringHelper.isEmpty(
          task.subProject.withPlacementNotes
        )
          ? 'Geen opmerkingen'
          : task.subProject.withPlacementNotes;
        task.timeBudgetDisplay =
          task.subProject.projectPlacement == null
            ? null
            : (
                (task.subProject.projectPlacement.installerHourBudget == null
                  ? 0
                  : task.subProject.projectPlacement.installerHourBudget * 60) +
                (task.subProject.projectPlacement.installerMinuteBudget == null
                  ? 0
                  : task.subProject.projectPlacement.installerMinuteBudget)
              ).toString();
      }
      if (task.subProject.projectPlacement.collectKey) {
        if (task.subProject.projectPlacement.collectKeyFrom === -1) {
          task.subProject.collectKeyFromAddress =
            task.subProject.projectPlacement.collectKeyExtraInfo;
        } else if (task.subProject.projectPlacement.collectKeyFrom != null) {
          const address = <any>(
            ProjectHelper.getKeyCollectionAddress(task.subProject)
          );
          task.subProject.collectKeyFromAddress =
            this.printAddressPipe.transform(
              address.line1,
              address.postalCode,
              address.city
            );
        }
      }
    }
  }

  preprocessOtherTask(task: OtherTask) {
    task.timeBudgetDisplay =
      task.minuteBudget == null ? null : task.minuteBudget.toString();
    task.titleDisplay = task.title;
  }

  preprocessLoadTask(task: LoadTask): void {
    task.timeBudgetDisplay =
      task.minuteBudget == null ? null : task.minuteBudget.toString();
    this.preprocessCompanies(task);
  }

  preprocessCompanies(task: SubProjectTask | LoadTask): void {
    const involvedCompanies = ProjectHelper.getDtoInvolvedCompanies(
      task.subProject.project
    );
    if (CollectionHelper.isPopulated(involvedCompanies)) {
      task.companiesDisplay = StringHelper.join(
        involvedCompanies.map((c) => c.regularName),
        ', '
      );
    }
  }

  openNotesModal(t: Task) {
    const config = {
      subProjectId: t.subProject.id,
      projectId: t.subProject.project.id,
    };
    this.dialogModalRef = this.dialog.open(PlacerNotesDialogComponent, {
      data: config,
    });
    this.addSubscription(
      this.dialogModalRef.afterClosed(),
      (project: Project) => {
        if (project != null) {
          this.processNotesResponse(t, project);
        }
      }
    );
  }

  processNotesResponse(t: Task, project: Project) {
    if (
      t.subProject.type === 'BaseProject' &&
      CollectionHelper.isPopulated(t.subProject.project.products)
    ) {
      for (let i = 0; i < t.subProject.project.products.length; i++) {
        const taskProduct = t.subProject.project.products[i];
        const projectProduct = project.products[i];
        if (taskProduct.productPlacement != null) {
          taskProduct.productPlacement.installerInfo = StringHelper.isEmpty(
            projectProduct.productPlacement.installerInfo
          )
            ? 'Geen opmerkingen'
            : projectProduct.productPlacement.installerInfo;
        }
      }
    }
    if (t.subProject.type !== 'BaseProject') {
      const projectSubproject = project.subProjects.find(
        (sp) => sp.id === t.subProject.id
      );
      if (projectSubproject != null) {
        t.subProject.withPlacementNotes = StringHelper.isEmpty(
          projectSubproject.withPlacementNotes
        )
          ? 'Geen opmerkingen'
          : projectSubproject.withPlacementNotes;
      }
    }
  }

  openCreateEditOtherTaskModal(task?: Task) {
    const config = {
      task: task,
      employeeId:
        this.selectedEmployee == null ? null : this.selectedEmployee.id,
      date: this.weekDates[this.selectedDateIndex],
      employeeChoices: this.employeeOptions,
    };
    this.createEditOtherTaskModelRef = this.dialog.open(
      CreateEditOtherTaskComponent,
      {
        data: config,
      }
    );
    this.addSubscription(
      this.createEditOtherTaskModelRef.afterClosed(),
      (task: OtherTask) => {
        if (task != null) {
          this.reloadData.emit();
        }
      }
    );
  }

  openEditLoadTaskModal(task: LoadTask): void {
    const cloned: LoadTask = _.cloneDeep(task);
    this.modalService.openComponentInModal(
      EditLoadTaskComponent,
      [
        { key: 'task', value: cloned },
        {
          key: 'employeeId',
          value:
            this.selectedEmployee == null ? null : this.selectedEmployee.id,
        },
        { key: 'date', value: this.weekDates[this.selectedDateIndex] },
        { key: 'employeeChoices', value: this.employeeOptions },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.saveSuccessEvent,
        (res: LoadTask) => {
          if (res != null) {
            this.reloadData.emit();
          }
        }
      )
    );
  }

  toggleTaskActive(task: LoadTask): void {
    const verb = task.active ? 'deactiveren' : 'activeren';
    this.modalService.confirmDialog(
      `Laadmoment ${verb}`,
      `Bent u zeker dat u dit laadmoment wilt ${verb}?`,
      (b) => {
        if (b) {
          task.active = !task.active;
          this.saveTaskList();
          this.calculateMapAddresses();
        }
      }
    );
  }
}
